import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";
import moment from "moment-timezone";
import clsx from "clsx";
import StyledPanel from "../Panels/StyledPanel";
import PaymentApprovedReceipt from "../Forms/PaymentApprovedReceipt";
import AccessGroupService from "../../services/AccessGroupService";
import { RateService } from "../../services/RateService";
import ContractService from "../../services/ContractService";
import FPAPaymentService from "../../services/FPAPaymentService";
import apiClient from "../../auth/apiClient";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import useCurrentFacilityTimezone from "../../hooks/useCurrentFacilityTimezone";
import { useStyles } from "./CloseTransactionPanel.styles";

const accessGroupService = new AccessGroupService(apiClient);
const rateService = new RateService(apiClient);
const contractService = new ContractService(apiClient);
const fpaPaymentService = new FPAPaymentService(apiClient);

// Requirement 6.6: the client waits at most 60 seconds for the payment API to respond.
const PAYMENT_TIMEOUT_MS = 60000;

const CloseTransactionPanel = ({ className, accessHolderID, accessHolderName, lastUse, onSuccess }) => {
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();
  const { facilityID } = useCurrentFacility();
  const { timeZone, convertToFacilityTimezoneUtc } = useCurrentFacilityTimezone();

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [entryTime, setEntryTime] = useState(null);
  const [exitTime, setExitTime] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [rateBlobID, setRateBlobID] = useState(null);
  const [accessGroupID, setAccessGroupID] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [failureOpen, setFailureOpen] = useState(false);
  const [calculatedFee, setCalculatedFee] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [approvedDetails, setApprovedDetails] = useState(null);

  const fetchTransactionDetails = useCallback(async () => {
    if (!accessHolderID || !facilityID) return;
    setLoading(true);
    try {
      const accountResponse = await contractService.getAccessHolder(facilityID, accessHolderID);
      const account = accountResponse?.data ?? null;
      setAccountDetails(account);

      const entrySource = lastUse ?? account?.startDate ?? null;
      setEntryTime(entrySource ? moment.utc(entrySource).tz(timeZone) : null);
      setExitTime(account?.endDate ? moment.utc(account.endDate).tz(timeZone) : null);

      const groupsResponse = await accessGroupService.GetAssignedAccessGroups(accessHolderID, 100, 0, "");
      const groups = groupsResponse?.data?.collection ?? groupsResponse?.data ?? [];
      const groupWithRate = groups.find((g) => g?.fpaSettings?.rateBlobID) ?? groups[0] ?? null;
      setRateBlobID(groupWithRate?.fpaSettings?.rateBlobID ?? null);
      setAccessGroupID(groupWithRate?.accessGroupID ?? groupWithRate?.fpaSettings?.accessGroupID ?? null);
    } catch {
      enqueueSnackbar("Failed to load transaction details", {
        variant: "error",
        tag: "closeTransactionLoadError",
      });
    } finally {
      setLoading(false);
    }
  }, [accessHolderID, facilityID, timeZone, lastUse]);

  useEffect(() => {
    setExitTime(null);
    setEntryTime(null);
    setAccountDetails(null);
    setRateBlobID(null);
    setAccessGroupID(null);
    setConfirmOpen(false);
    setFailureOpen(false);
    setCalculatedFee(null);
    setReceipt(null);
    setApprovedDetails(null);
    setProcessing(false);
    fetchTransactionDetails();
  }, [fetchTransactionDetails]);

  const handleProcessExit = async () => {
    setProcessing(true);
    try {
      const requestBody = {
        BlobID: rateBlobID,
        EntryTime: entryTime ? convertToFacilityTimezoneUtc(entryTime).toISOString() : null,
        ExitTime: convertToFacilityTimezoneUtc(exitTime).toISOString(),
        EntityID: facilityID,
      };
      const rateResponse = await rateService.CalculateWithTaxes(requestBody, false, false);
      const fee = rateResponse?.data?.totalToPay ?? 0;
      setCalculatedFee(fee);
      setReceipt(rateResponse?.data ?? null);
      setConfirmOpen(true);
    } catch {
      enqueueSnackbar("Failed to calculate parking fee", {
        variant: "error",
        tag: "closeTransactionRateError",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmYes = async () => {
    setConfirmOpen(false);
    setProcessing(true);

    let timedOut = false;
    const timeout = new Promise((_, reject) => {
      setTimeout(() => {
        timedOut = true;
        reject(new Error("timeout"));
      }, PAYMENT_TIMEOUT_MS);
    });

    try {
      const accountCredential = (accountDetails?.credentials ?? []).find((cred) => cred?.credentialID) ?? null;

      const paymentRequest = fpaPaymentService.requestCloseTransactionPayment({
        amount: calculatedFee,
        accessHolderID: accessHolderID,
        contractID: accountDetails?.contractID ?? null,
        facilityID: facilityID,
        accessGroupID: accessGroupID ?? null,
        credentialID: accountCredential?.credentialID ?? null,
        credentialReference: accountCredential?.credentialReference ?? null,
        entryTime: entryTime ? convertToFacilityTimezoneUtc(entryTime).toISOString() : null,
        exitTime: exitTime ? convertToFacilityTimezoneUtc(exitTime).toISOString() : null,
        receipt: receipt ?? null,
      });

      const response = await Promise.race([paymentRequest, timeout]);

      if (response?.data?.isPaymentSuccessful === true) {
         await persistExitAndStatus();
        setApprovedDetails(response.data);
        return;
      }

      setFailureOpen(true);
    } catch (error) {
      if (timedOut) {
        enqueueSnackbar(
          "The transaction could not be confirmed. Please try again.",
          { variant: "error", tag: "closeTransactionTimeoutError" }
        );
      } else if (error?.response?.status === 404) {
        // Requirement 7.1: HTTP 404 (gateway unset / no card on file) is treated
        // as a payment failure; the transaction stays open.
        setFailureOpen(true);
      } else {
        enqueueSnackbar("Failed to process exit", {
          variant: "error",
          tag: "closeTransactionExitError",
        });
      }
    } finally {
      setProcessing(false);
    }
  };

  const persistExitAndStatus = async () => {
    try {
      if (accountDetails && exitTime) {
        await contractService.updateAccessHolder({
          ...accountDetails,
          status: "Out",
          startDate:accountDetails.startDate.replace(/\.\d+$/, ".000") + "Z",
        });
      }
    } catch {
      // The payment already succeeded; ignore exit-time persistence failures so the
      // receipt still shows and the card is never re-charged.
    }
  };

  const handleFailureClose = () => {
    // Requirement 7.4: closing the failure dialog returns to the drawer with the
    // transaction still open and the I/O status unchanged.
    setFailureOpen(false);
  };

  const handleApprovedClose = () => {
    setApprovedDetails(null);
    if (onSuccess) onSuccess();
  };

  const nowInFacilityTz = moment.tz(timeZone);
  const isExitTimeValid =
    !!exitTime &&
    exitTime.isValid() &&
    (!entryTime || exitTime.isAfter(entryTime)) &&
    !exitTime.isAfter(nowInFacilityTz);

  return (
    <>
      <StyledPanel
        className={clsx("close-transaction-panel", className)}
        headerContent={
          <Typography variant="subtitle1">Close Transaction</Typography>
        }
        cardContent={
          loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <div className={classes.fieldsContainer}>
              <DateTimePicker
                label="Entry Time"
                value={entryTime}
                format="MM/DD/YY hh:mm a"
                disabled
                inputVariant="outlined"
                size="small"
                className="flex-input close-transaction-entry-time"
              />
              <DateTimePicker
                label="Exit Time"
                value={exitTime}
                onChange={setExitTime}
                format="MM/DD/YY hh:mm a"
                disableFuture
                minDate={entryTime || undefined}
                inputVariant="outlined"
                size="small"
                className="flex-input close-transaction-exit-time"
              />
              <div className={classes.footer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleProcessExit}
                  disabled={processing || !isExitTimeValid}
                  startIcon={processing ? <CircularProgress size={14} color="inherit" /> : undefined}
                  className="close-transaction-process-exit-btn"
                  data-testid="process-exit-button"
                >
                  {processing ? "Processing..." : "Process Exit"}
                </Button>
              </div>
            </div>
          )
        }
      />

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        className="close-transaction-confirm-dialog"
      >
        <DialogContent>
          <DialogContentText>
            {`This action will charge $${calculatedFee?.toFixed(2) ?? "0.00"} to this access holder's Flexible Parking Account or credit card on file and will set their I/O status to Out.`}
            <br /><br />
            Do you wish to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirmYes}
            data-testid="confirm-yes-button"
          >
            Yes
          </Button>
          <Button
            variant="contained"
            onClick={() => setConfirmOpen(false)}
            data-testid="confirm-no-button"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={failureOpen}
        onClose={handleFailureClose}
        className="close-transaction-failure-dialog"
      >
        <DialogTitle className="close-transaction-failure-title">
          Unable to Close Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The transaction cannot be closed because payment could not be completed.
            Please update the payment method or collect an alternate form of payment
            before closing the transaction.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={handleFailureClose}
            data-testid="failure-ok-button"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment-approved receipt. Reuses the same receipt shown by the on-file
          Add Funds flow (transaction number, total paid, last 4, card type, and
          authorization code). Closing it closes the drawer and refreshes the list. */}
      <Dialog
        open={!!approvedDetails}
        onClose={handleApprovedClose}
        className="close-transaction-approved-dialog"
      >
        <DialogContent>
          <PaymentApprovedReceipt
            transactionDetails={approvedDetails}
            onClose={handleApprovedClose}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

CloseTransactionPanel.propTypes = {
  className: PropTypes.string,
  accessHolderID: PropTypes.string,
  accessHolderName: PropTypes.string,
  lastUse: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default CloseTransactionPanel;
